import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, HeartPulse, Download, Crown, Sparkles, Copy } from "lucide-react";

export function NhsSupportingDialog({ app, isPro }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [current, setCurrent] = useState(null);

  const canGen = !!(app?.tailored_cv_id && app?.job_description);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/nhs/supporting-info");
      const mine = (Array.isArray(data) ? data : []).filter((d) => d.application_id === app.id);
      const done = mine.find((d) => d.status === "done");
      if (done) { const full = await api.get(`/nhs/supporting-info/${done.id}`); setCurrent(full.data); }
    } catch (e) { /* pro gate handled in UI */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (open && isPro && canGen) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/nhs/supporting-info/generate", { application_id: app.id });
      const id = data.id;
      let finished = false;
      for (let i = 0; i < 90; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data: d } = await api.get(`/nhs/supporting-info/${id}`);
        if (d.status === "done") { setCurrent(d); toast.success("Supporting statement ready"); finished = true; break; }
        if (d.status === "error") { toast.error("Generation failed. Please try again."); finished = true; break; }
      }
      if (!finished) toast.message("Still working — check back shortly.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Generation failed");
    } finally { setGenerating(false); }
  };

  return (
    <>
      <Button
        variant="ghost" size="icon"
        className="h-8 w-8 text-[#005EB8] disabled:opacity-40"
        onClick={() => setOpen(true)}
        disabled={!canGen}
        title={canGen ? "NHS Supporting Information" : "Tailor a CV for this application first."}
        data-testid={`nhs-statement-${app.id}`}
      >
        <HeartPulse className="h-4 w-4" strokeWidth={1.5} />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="nhs-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><HeartPulse className="h-5 w-5 text-[#005EB8]" /> NHS Supporting Information — {app?.job_title} · {app?.company}</DialogTitle></DialogHeader>

          {!isPro ? (
            <div className="text-center py-6" data-testid="nhs-pro-gate">
              <div className="h-12 w-12 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-3"><Crown className="h-6 w-6 text-accent" /></div>
              <h3 className="font-heading text-xl font-bold">NHS Supporting Information is a Pro feature</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">Generate a ~1,400-word statement mapped to the NHS person specification, grounded in your tailored CV.</p>
              <Button className="mt-4" onClick={() => navigate("/billing")} data-testid="nhs-upgrade-btn"><Crown className="h-4 w-4 mr-1.5" /> Upgrade to Pro</Button>
            </div>
          ) : loading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : !current ? (
            <div className="text-center py-6">
              <p className="text-sm text-muted-foreground max-w-md mx-auto mb-4">We'll write a ~1,400-word supporting statement mapped to this role's person specification (its job description), grounded entirely in your tailored CV. We never invent experience or registrations.</p>
              <Button onClick={generate} disabled={generating} data-testid="nhs-dialog-generate-btn" className="active:scale-[0.98] transition-transform">
                {generating ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Writing…</> : <><Sparkles className="h-4 w-4 mr-1.5" /> Generate supporting statement</>}
              </Button>
              <p className="text-xs text-muted-foreground mt-2">5 generations/day</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Approx. {current.word_count} words · review & personalise before submitting</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={async () => { try { await navigator.clipboard.writeText(current.supporting_info || ""); toast.success("Copied — paste it straight into TRAC"); } catch { toast.error("Couldn't copy."); } }} data-testid="nhs-dialog-copy"><Copy className="h-4 w-4 mr-1.5" /> Copy</Button>
                  <a href={`${API}/nhs/supporting-info/${current.id}/download?fmt=docx`} target="_blank" rel="noreferrer" data-testid="nhs-dialog-download-word"><Button variant="outline" size="sm"><Download className="h-4 w-4 mr-1.5" /> Word</Button></a>
                  <a href={`${API}/nhs/supporting-info/${current.id}/download?fmt=pdf`} target="_blank" rel="noreferrer" data-testid="nhs-dialog-download"><Button variant="outline" size="sm"><Download className="h-4 w-4 mr-1.5" /> PDF</Button></a>
                  <Button variant="outline" size="sm" onClick={generate} disabled={generating} data-testid="nhs-dialog-regenerate">{generating ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />} New</Button>
                </div>
              </div>
              <div className="text-sm whitespace-pre-wrap leading-relaxed bg-zinc-50 border rounded-lg p-4" data-testid="nhs-dialog-text">{current.supporting_info}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
